import config from 'config';
import * as FullStory from '@fullstory/browser';

declare function FS(command: 'setIdentity', options: { uid: string; properties?: object }): void;

/**
 * Initialize FullStory with proper configuration
 */
export const initialize = (): boolean => {
  try {
    if (!config.FULLSTORY?.enabled) {
      return false;
    }

    FullStory.init({
      orgId: 'QVPYG',
      devMode: false,
    });

    return true;
  } catch {
    return false;
  }
};

/**
 * Internal function to call FullStory.setIdentity
 */
const setIdentityInternal = (uid: string, properties: object): boolean => {
  if (!config.FULLSTORY?.enabled) {
    return false;
  }

  if (!uid) {
    return false;
  }

  const userProperties: object = typeof properties === 'object' && properties !== null ? properties : {};

  try {
    FS('setIdentity', { uid, properties: userProperties });

    return true;
  } catch {
    return false;
  }
};

interface UsersStore {
  currUserName: string | null | undefined;
  getUserDataForSegment: () => {
    userName?: string;
    email?: string;
    companyName?: string;
    account?: string;
    isMSP?: boolean;
    isReseller?: boolean;
    isResellerCustomer?: boolean;
    isNew?: boolean;
    accountType?: string;
    accountId?: string;
  };
}

/**
 * Identify the user in FullStory with detailed properties from usersStore.
 */
export const identifyUser = (userKey: string, usersStore: UsersStore): boolean => {
  if (!config.FULLSTORY?.enabled) {
    return false;
  }

  if (!userKey) {
    return false;
  }

  if (!usersStore) {
    return false;
  }

  try {
    if (!usersStore.currUserName) {
      return false;
    }

    const segmentData = usersStore.getUserDataForSegment();

    const properties: {
      displayName?: string;
      email?: string;
      org?: string;
      accountName_fs?: string;
      isMSP_fs?: boolean;
      isReseller_fs?: boolean;
      isResellerCustomer_fs?: boolean;
      isNewUser_fs?: boolean;
      accountType_fs?: string;
      accountId_fs?: string;
    } = {
      displayName: segmentData.userName,
      email: segmentData.email,
      org: segmentData.companyName,
      accountName_fs: segmentData.account,
      isMSP_fs: segmentData.isMSP,
      isReseller_fs: segmentData.isReseller,
      isResellerCustomer_fs: segmentData.isResellerCustomer,
      isNewUser_fs: segmentData.isNew,
      accountType_fs: segmentData.accountType,
      accountId_fs: segmentData.accountId,
    };

    Object.keys(properties).forEach((key) => {
      if (
        properties[key as keyof typeof properties] === null ||
        properties[key as keyof typeof properties] === undefined
      ) {
        delete properties[key as keyof typeof properties];
      }
    });

    const setResult = setIdentityInternal(userKey, properties);
    return setResult;
  } catch {
    return false;
  }
};
